/**
 * @module builder
 */
import type { OptionsDocument, PresentationSectionV2Option } from 'paintscout'
import { getPrefabSections } from '../../../../database/fixtures/presentations/prefabSections'
import { ADDABLE_SHERWIN_WILLIAMS_PAGES } from '../sherwin-williams-section-types'
import { uuid } from '../../../util'

/**
 * Sherwin-Williams partner content (#21311), offered as whole **sections** — the bigger unit — not
 * as individual blocks. Each section wraps a single static partner block (the v1 components, reused
 * by v2 — see react-quote PresentationNext/sections/partners/).
 *
 * These were "page options" before #21990; only the level's name changed.
 */
export function getSherwinWilliamsSectionOptions(): PresentationSectionV2Option[] {
  return ADDABLE_SHERWIN_WILLIAMS_PAGES.map(({ type, label }) => ({
    type,
    label,
    title: label,
    active: true,
    categories: ['sherwin-williams'],
    advanced: {
      useForNav: true,
      allowCustom: false
    },
    blocks: [
      {
        key: uuid(),
        type,
        label,
        title: label,
        active: true,
        advanced: {
          useForNav: true,
          allowCustom: false
        }
      }
    ]
  }))
}

/**
 * The v2 section catalog — what the "Add Section" dialog offers: prefab bundles of blocks, plus the
 * Sherwin-Williams partner sections.
 *
 * This is the v2 counterpart of v1's getPresentationPageOptions (which used to serve both versions
 * via an `isNewPresentation` flag). Unlike the #21312 v1-parity blocks, the partner sections are
 * neither feature-gated nor superadmin-gated: every account gets them (#23966). They used to need
 * `partnerContent.enabled && partnerContent.sherwinWilliams.presentationPages.enabled`, but the
 * seed fixtures wrote both as `false`, so accounts silently shipped without them.
 *
 * `options` is still accepted — callers thread it through — but nothing in this catalog is
 * account-gated any more.
 */
export function getPresentationNextSectionOptions(args: {
  options?: OptionsDocument
  type?: string
}): PresentationSectionV2Option[] {
  const { type } = args

  let sectionOptions = [...getPrefabSections(), ...getSherwinWilliamsSectionOptions()]

  // `label` isn't unique within a category (every "about" entry is titled "About Us"), so
  // anything that needs to look a specific catalog entry back up — e.g. the SectionVariantsFlyout
  // drag payload — must key on this instead. Position in this concatenated array is stable because
  // prefab sections always come first, in the same fixed order, then the SW sections.
  sectionOptions = sectionOptions.map((option, index) => ({
    ...option,
    optionKey: option.optionKey ?? `${option.type}-${index}`
  }))

  if (type) {
    return sectionOptions.filter((option) => option.type === type)
  }

  return sectionOptions
}
